import { Loader2 } from "lucide-react";
import { useState } from "react";
import { toast } from "react-toastify";
import { sendNotification } from "src/redux/api/_hr.api";
import { Employee } from "src/redux/types/_employee.type";

interface NotificationProps {
  employees: Employee[];
}

const Notification = ({ employees }: NotificationProps) => {
  const [selectedCheckboxes, setSelectedCheckboxes] = useState<Set<number>>(
    new Set()
  );
  const [clickedEmployee, setClickedEmployee] = useState<Employee | null>(null);
  const [header, setHeader] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const toggleCheckbox = (id: number) => {
    setClickedEmployee(null); // Clear single-click selection if checkbox is used
    setSelectedCheckboxes((prev) => {
      const newSet = new Set(prev);
      newSet.has(id) ? newSet.delete(id) : newSet.add(id);
      return newSet;
    });
  };

  const handleCardClick = (employee: Employee) => {
    // If already using checkboxes, ignore card click
    if (selectedCheckboxes.size > 0) return;
    setClickedEmployee(employee);
  };

  const getSelectedEmployees = () => {
    if (selectedCheckboxes.size > 0) {
      return employees.filter((emp) => selectedCheckboxes.has(emp.id));
    } else if (clickedEmployee) {
      return [clickedEmployee];
    }
    return [];
  };

  const handleSend = async () => {
    const selected = getSelectedEmployees();

    if (selected.length === 0 || !header || !message) {
      alert("Please select employee(s) and fill in all fields.");
      return;
    }
    const ids = selected.map((e) => e.id);
    try {
      setLoading(true);
      const response = await sendNotification(ids, message, header);

      if (response.message.toLowerCase() === "success") {
        toast.success("Notification Sent!");
        // Reset
        setHeader("");
        setMessage("");
        setSelectedCheckboxes(new Set());
        setClickedEmployee(null);
      }
    } catch (error) {
      toast.error("Failed to Send Notification!");
      console.error("Failed to send Notification", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = () => {
    if (selectedCheckboxes.size === employees.length) {
      setSelectedCheckboxes(new Set()); // Deselect all
    } else {
      setClickedEmployee(null); // Exit single select mode
      setSelectedCheckboxes(new Set(employees.map((emp) => emp.id))); // Select all
    }
  };

  const handleClear = () => {
    setSelectedCheckboxes(new Set());
    setClickedEmployee(null);
  };

  const selectedList = getSelectedEmployees();
  const allSelected = selectedCheckboxes.size === employees.length;

  return (
    <div className="grid grid-cols-2 h-full gap-6">
      {/* Left Column: Employee List */}
      <div className="rounded-2xl shadow-md shadow-gray-400 p-4 overflow-y-auto">
        <div className="grid grid-cols-2 p-2">
          <button
            className="text-sm text-blue-600 px-3 py-1 hover:shadow-md shadow-gray-400 rounded-2xl"
            onClick={handleClear}
          >
            Clear Selection
          </button>
          <button
            className="text-sm text-blue-600 px-3 py-1 hover:shadow-md shadow-gray-400 rounded-2xl"
            onClick={handleSelectAll}
          >
            {allSelected ? "Deselect All" : "Select All"}
          </button>
        </div>
        {employees.map((emp) => {
          const isChecked = selectedCheckboxes.has(emp.id);
          const isClicked =
            clickedEmployee?.id === emp.id && selectedCheckboxes.size === 0;

          return (
            <div
              key={emp.id}
              className={`flex gap-5 p-3 mb-4 rounded-lg border cursor-pointer transition ${
                isChecked || isClicked
                  ? "bg-blue-100 border-blue-400 shadow-md shadow-gray-400"
                  : "hover:bg-gray-100 border-gray-300"
              }`}
              onClick={() => handleCardClick(emp)}
            >
              <div className="flex flex-col items-center justify-center">
                <input
                  type="checkbox"
                  className="size-5"
                  checked={isChecked}
                  onChange={(e) => toggleCheckbox(emp.id)}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
              <div className="flex gap-8 items-center justify-center">
                <div>
                  <p className="font-medium">
                  {emp.full_name}
                </p>
                <p className="text-sm text-gray-500">{emp.email}</p>
                </div>
                <div className="flex gap-3">
                  <p className="text-md text-gray-400">{emp.department}</p>
                  <p className="text-md text-gray-400 font-bold">|</p>
                  {/* <p className="text-md text-gray-400">{emp.state}</p> */}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Right Column: Notification Input */}
      <div className="bg-white rounded-2xl shadow-md shadow-gray-400 p-6">
        {selectedList.length > 0 ? (
          <>
            <h2 className="text-xl font-semibold mb-4">
              Send Notification to{" "}
              {selectedList.length === 1
                ? `${selectedList[0].full_name} `
                : `${selectedList.length} employees`}
            </h2>
            <div className="mb-4">
              <label className="block mb-1 font-medium">Header</label>
              <input
                type="text"
                className="w-full border rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={header}
                onChange={(e) => setHeader(e.target.value)}
                placeholder="Enter notification header"
              />
            </div>
            <div className="mb-4">
              <label className="block mb-1 font-medium">Message</label>
              <textarea
                className="w-full border rounded-lg p-2 h-32 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Enter your message"
              />
            </div>
            <div className="flex justify-center items-center">
              <button
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
                onClick={handleSend}
                disabled={loading}
              >
                { loading ? (<Loader2/>) : "Send Notification"}
              </button>
            </div>
          </>
        ) : (
          <div className="text-gray-400 text-center mt-32 text-lg">
            Select an employee to compose a message
          </div>
        )}
      </div>
    </div>
  );
};

export default Notification;
